import React, { Suspense, lazy } from "react";
import { Navigate, createBrowserRouter, useParams } from "react-router-dom";

/** SHARED LAYOUTS & GUARDS (Static Imports) */
import Userlayout from "./modules/user/shared/userlayout";
import UserPublicRoute from "./modules/user/shared/userpublicroute";
import UserPrivateRoute from "./modules/user/shared/userprivateroute";
import Adminlayout from "./modules/admin/shared/adminlayout";
import PrivateRoute from "./modules/admin/shared/privateRoute";
import PublicRoute from "./modules/admin/shared/publicRoute";
import FrontRoot from "./modules/user/shared/frontroot";
import Header from "./modules/user/shared/header";
import Footer from "./modules/user/shared/footer";
import BackOfficeLayout from "./modules/BackOffice/shared/backOfficeLayout";
import BackOfficePrivateRoute from "./modules/BackOffice/shared/backOfficePrivateRoute";
import LMUserslayout from "./modules/LMUsers/shared/lmuserslayout";
import LMUsersPrivateRoute from "./modules/LMUsers/shared/lmusersprivateroute";
import SISUserLayout from "./modules/SISUsers/shared/layout";
import SISUserPrivateRoute from "./modules/SISUsers/shared/privateRoute";
import InfoCounterLayout from "./modules/InfoCounter/shared/layout";
import InfoCounterPrivateRoute from "./modules/InfoCounter/shared/privateRoute";
import InfoCounterHeader from "./modules/InfoCounter/shared/header";
import InfoCounterFooter from "./modules/InfoCounter/shared/footer";
import { STAFF_ROLE } from "./utils/Constants";
import { getUser } from "./utils/commonfunction";

// Helper component for dynamic lazy loading with spinner fallback
const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const Loadable = (Component) => (props) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);


/** USER ROUTE LAZY IMPORTS */
const Home = Loadable(lazy(() => import("./modules/user/pages/home")));
const UserLogin = Loadable(lazy(() => import("./modules/user/pages/login")));
const UserProfile = Loadable(lazy(() => import("./modules/user/pages/profile")));
const UserForgetPassword = Loadable(lazy(() => import("./modules/user/pages/forgetpassword")));
const UserOtpVerification = Loadable(lazy(() => import("./modules/user/pages/otpverification")));
const ResetPassword = Loadable(lazy(() => import("./modules/user/pages/resetpwd")));
const EventDetailsPage = Loadable(lazy(() => import("./modules/user/pages/EventDetails/eventDetailspage")));
const UserTopics = Loadable(lazy(() => import("./modules/user/pages/activityform/topics")));
const UserActivity = Loadable(lazy(() => import("./modules/user/pages/activityform/activityform")));
const UserTopicsView = Loadable(lazy(() => import("./modules/user/pages/activityform/topicsView")));
const SurveyTable = Loadable(lazy(() => import("./modules/user/pages/surve/surveytable")));
const SurveyQuestions = Loadable(lazy(() => import("./modules/user/pages/surve/questionset")));
const UserAnnouncements = Loadable(lazy(() => import("./modules/user/pages/announcements/announcements")));
const UserNotifications = Loadable(lazy(() => import("./modules/user/pages/notification")));
const UserEvents = Loadable(lazy(() => import("./modules/user/pages/events")));
const FAQ = Loadable(lazy(() => import("./modules/user/pages/faq")));
const PrivacyPolicy = Loadable(lazy(() => import("./modules/user/pages/privacypolicy")));
const TermsCondition = Loadable(lazy(() => import("./modules/user/pages/termscondition")));
const CompleteProfile = Loadable(lazy(() => import("./modules/user/pages/completeprofile")));
const DynamicPages = Loadable(lazy(() => import("./modules/user/pages/dynamicPages")));
const Registration = Loadable(lazy(() => import("./modules/user/pages/Registration")));
const RegistrationStepTwo = Loadable(lazy(() => import("./modules/user/pages/RegistrationStepTwo")));
const UserChangePassword = Loadable(lazy(() => import("./modules/user/pages/changepassword")));
const SharedFiles = Loadable(lazy(() => import("./modules/user/pages/sharedFiles")));
const EventRegistrationList = Loadable(lazy(() => import("./modules/user/pages/eventRegistrationList")));
const UserSetting = Loadable(lazy(() => import("./modules/user/pages/userSetting")));
const Error404User = Loadable(lazy(() => import("./modules/user/pages/user404")));
const Settings = Loadable(lazy(() => import("./modules/user/pages/settings")));
const EventRegistrationView = Loadable(lazy(() => import("./modules/user/pages/eventRegistrationView")));
const InvitationList = Loadable(lazy(() => import("./modules/user/pages/invitationList")));
const RegistrationThanksPage = Loadable(lazy(() => import("./modules/user/pages/registrationThanksPage")));
const InvitationRejectPage = Loadable(lazy(() => import("./modules/user/pages/invitationRejectPage")));
const Chat = Loadable(lazy(() => import('./modules/user/pages/chatComponents/Chat/Chat')));
const Join = Loadable(lazy(() => import('./modules/user/pages/chatComponents/Join/Join')));
const TransactionList = Loadable(lazy(() => import("./modules/user/pages/transaction")));
const EventDetailsPreviewAdmin = Loadable(lazy(() => import("./modules/user/pages/EventDetails/eventDetailsPreviewAdmin")));
const InvoicePaymentPage = Loadable(lazy(() => import("./modules/user/pages/inovicepaymentPage")));
const UserViewAnnouncements = Loadable(lazy(() => import("./modules/user/pages/announcements/announcementsView")));
const NewTestPages = Loadable(lazy(() => import("./modules/user/pages/new-page-design")));
const SurveyThanks = Loadable(lazy(() => import("./modules/user/pages/surve/SurveyThanks")));
const StaffRooms = Loadable(lazy(() => import("./modules/user/pages/staff/room")));
const StaffUserBooking = Loadable(lazy(() => import("./modules/user/pages/staff/booking")));
const StaffMyBookings = Loadable(lazy(() => import("./modules/user/pages/staff/myBooking")));
const ViewStaffBooking = Loadable(lazy(() => import("./modules/user/pages/staff/bookingDetail")));
const StaffEditBooking = Loadable(lazy(() => import("./modules/user/pages/staff/editBooking")));

/** ADMIN ROUTE LAZY IMPORTS */
const Login = Loadable(lazy(() => import("./modules/admin/pages/login")));
const Profile = Loadable(lazy(() => import("./modules/admin/pages/profile")));
const EditProfile = Loadable(lazy(() => import("./modules/admin/pages/editprofile")));
const Changepassword = Loadable(lazy(() => import("./modules/admin/pages/changepassword")));
const OtpVerificationPage = Loadable(lazy(() => import("./modules/admin/pages/otpverification")));
const ForgotPwdPage = Loadable(lazy(() => import("./modules/admin/pages/forgotpassword")));
const ResetPwdPage = Loadable(lazy(() => import("./modules/admin/pages/resetpwd")));
const Error404 = Loadable(lazy(() => import("./modules/admin/pages/404")));
const Dashboard = Loadable(lazy(() => import("./modules/admin/pages/dashboard")));
const PortalDesign = Loadable(lazy(() => import("./modules/admin/pages/portaldesign")));
const EmailTemplatesTable = Loadable(lazy(() => import("./modules/admin/pages/cms/automaticemailtemplates/list")));
const EmailTempAdd = Loadable(lazy(() => import("./modules/admin/pages/cms/automaticemailtemplates/add")));
const EmailTempEdit = Loadable(lazy(() => import("./modules/admin/pages/cms/automaticemailtemplates/edit")));
const ViewEmailTemp = Loadable(lazy(() => import("./modules/admin/pages/cms/automaticemailtemplates/view")));
const PageTable = Loadable(lazy(() => import("./modules/admin/pages/cms/pages/list")));
const PageAdd = Loadable(lazy(() => import("./modules/admin/pages/cms/pages/add")));
const PageEdit = Loadable(lazy(() => import("./modules/admin/pages/cms/pages/edit")));
const PageView = Loadable(lazy(() => import("./modules/admin/pages/cms/pages/view")));
const GlobalSettings = Loadable(lazy(() => import("./modules/admin/pages/cms/settings/settings")));
const QuestionSetTable = Loadable(lazy(() => import("./modules/admin/pages/questionset/list")));
const QuestionSetAdd = Loadable(lazy(() => import("./modules/admin/pages/questionset/add")));
const QuestionSetEdit = Loadable(lazy(() => import("./modules/admin/pages/questionset/edit")));
const QuestionSetView = Loadable(lazy(() => import("./modules/admin/pages/questionset/view")));
const QuestionAdd = Loadable(lazy(() => import("./modules/admin/pages/question/add")));
const QuestionEdit = Loadable(lazy(() => import("./modules/admin/pages/question/edit")));
const QuestionView = Loadable(lazy(() => import("./modules/admin/pages/question/view")));
const TicketAdd = Loadable(lazy(() => import("./modules/admin/pages/ticket/add")));
const TicketEdit = Loadable(lazy(() => import("./modules/admin/pages/ticket/edit")));
const TicketView = Loadable(lazy(() => import("./modules/admin/pages/ticket/view")));
const TicketTable = Loadable(lazy(() => import("./modules/admin/pages/ticket/list")));
const EventsTable = Loadable(lazy(() => import("./modules/admin/pages/events/list")));
const EventsAdd = Loadable(lazy(() => import("./modules/admin/pages/events/add")));
const EventsEdit = Loadable(lazy(() => import("./modules/admin/pages/events/edit")));
const View = Loadable(lazy(() => import("./modules/admin/pages/events/view")));
const ViewEvent = Loadable(lazy(() => import("./modules/admin/pages/events/viewEvent")));
const ViewHotels = Loadable(lazy(() => import("./modules/admin/pages/events/viewHotels")));
const ViewAgenda = Loadable(lazy(() => import("./modules/admin/pages/events/viewAgenda")));
const ViewRegistrations = Loadable(lazy(() => import("./modules/admin/pages/events/viewRegistrations")));
const ViewInvitations = Loadable(lazy(() => import("./modules/admin/pages/events/viewInvitations")));
const ViewAttendees = Loadable(lazy(() => import("./modules/admin/pages/events/viewAttendees")));
const ViewRegistrationDetails = Loadable(lazy(() => import("./modules/admin/pages/events/viewRegistrationDetails")));
const ViewInvoiceDetails = Loadable(lazy(() => import("./modules/admin/pages/events/viewInvoice")));
const Event = Loadable(lazy(() => import("./modules/admin/pages/events/event")));
const Hotels = Loadable(lazy(() => import("./modules/admin/pages/events/hotels")));
const Agenda = Loadable(lazy(() => import("./modules/admin/pages/events/agenda")));
const ActivitySetTable = Loadable(lazy(() => import("./modules/admin/pages/activityset/list")));
const ActivitySetAdd = Loadable(lazy(() => import("./modules/admin/pages/activityset/add")));
const ActivitySetEdit = Loadable(lazy(() => import("./modules/admin/pages/activityset/edit")));
const ActivitySetView = Loadable(lazy(() => import("./modules/admin/pages/activityset/view")));
const TopicAdd = Loadable(lazy(() => import("./modules/admin/pages/topic/add")));
const TopicEdit = Loadable(lazy(() => import("./modules/admin/pages/topic/edit")));
const TopicView = Loadable(lazy(() => import("./modules/admin/pages/topic/view")));
const BannerTable = Loadable(lazy(() => import("./modules/admin/pages/cms/banners/list")));
const BannerAdd = Loadable(lazy(() => import("./modules/admin/pages/cms/banners/add")));
const BannerEdit = Loadable(lazy(() => import("./modules/admin/pages/cms/banners/edit")));
const BannerView = Loadable(lazy(() => import("./modules/admin/pages/cms/banners/view")));
const NewsLettersTable = Loadable(lazy(() => import("./modules/admin/pages/newsletters/list")));
const NewsLettersAdd = Loadable(lazy(() => import("./modules/admin/pages/newsletters/add")));
const NewsLettersEdit = Loadable(lazy(() => import("./modules/admin/pages/newsletters/edit")));
const NewsLettersView = Loadable(lazy(() => import("./modules/admin/pages/newsletters/view")));
const Galary = Loadable(lazy(() => import("./modules/admin/pages/galary/galary")));
const GalaryDetails = Loadable(lazy(() => import("./modules/admin/pages/galary/galarydetails")));
const UsersTable = Loadable(lazy(() => import("./modules/admin/pages/usermgmt/list")));
const UsersView = Loadable(lazy(() => import("./modules/admin/pages/usermgmt/view")));
const GalleryAdd = Loadable(lazy(() => import("./modules/admin/pages/galary/add")));
const PromotionsList = Loadable(lazy(() => import("./modules/admin/pages/newsletters/promotions/list")));
const PromotionsView = Loadable(lazy(() => import("./modules/admin/pages/newsletters/promotions/view")));
const InvitationsList = Loadable(lazy(() => import("./modules/admin/pages/events/Invitations/list")));
const InvitationView = Loadable(lazy(() => import("./modules/admin/pages/events/Invitations/view")));
const EmailLogs = Loadable(lazy(() => import("./modules/admin/pages/emaillogs/list")));
const StaffTable = Loadable(lazy(() => import("./modules/admin/pages/staff/list")));
const StaffAdd = Loadable(lazy(() => import("./modules/admin/pages/staff/add")));
const StaffsEdit = Loadable(lazy(() => import("./modules/admin/pages/staff/edit")));
const StaffView = Loadable(lazy(() => import("./modules/admin/pages/staff/view")));
const EmailLogsView = Loadable(lazy(() => import("./modules/admin/pages/emaillogs/view")));
const EmailTempAddNews = Loadable(lazy(() => import("./modules/admin/pages/cms/emailTemplate/add")));
const EmailTempEditNews = Loadable(lazy(() => import("./modules/admin/pages/cms/emailTemplate/edit")));
const EmailTemplatesTableNews = Loadable(lazy(() => import("./modules/admin/pages/cms/emailTemplate/list")));
const ViewEmailTempNews = Loadable(lazy(() => import("./modules/admin/pages/cms/emailTemplate/view")));
const UsersAdd = Loadable(lazy(() => import("./modules/admin/pages/usermgmt/add")));
const UserEdit = Loadable(lazy(() => import("./modules/admin/pages/usermgmt/edit")));
const CompleteAdminProfile = Loadable(lazy(() => import("./modules/admin/pages/completeAdminProfile")));
const InvoiceDesign = Loadable(lazy(() => import("./modules/admin/pages/invoiceDesign")));
const EditRegistrationDetail = Loadable(lazy(() => import("./modules/admin/pages/events/editRegistrationDetails")));
const ViewTicketDetails = Loadable(lazy(() => import("./modules/admin/pages/events/checkIn-checkOut")));
const ViewAttendeesDetails = Loadable(lazy(() => import("./modules/admin/pages/events/attendeesView")));
const EditRegistrationDetailStepTwo = Loadable(lazy(() => import("./modules/admin/pages/events/EditRegistrationDetailStepTwo")));
const TransactionsTable = Loadable(lazy(() => import("./modules/admin/pages/Transactions/list")));
const TransactionsView = Loadable(lazy(() => import("./modules/admin/pages/Transactions/view")));
const EventTransactions = Loadable(lazy(() => import("./modules/admin/pages/events/eventTransactions")));
const PushNotificationTable = Loadable(lazy(() => import("./modules/admin/pages/cms/pushNotificationTemplate/list")));
const PushNotificationAdd = Loadable(lazy(() => import("./modules/admin/pages/cms/pushNotificationTemplate/add")));
const ViewPushNotification = Loadable(lazy(() => import("./modules/admin/pages/cms/pushNotificationTemplate/view")));
const PushNotificationEdit = Loadable(lazy(() => import("./modules/admin/pages/cms/pushNotificationTemplate/edit")));
const LanguageFiles = Loadable(lazy(() => import("./modules/admin/pages/cms/languageFiles/languageFiles")));
const FrontendFiles = Loadable(lazy(() => import("./modules/admin/pages/cms/languageFiles/frontendFiles")));
const BackendFiles = Loadable(lazy(() => import("./modules/admin/pages/cms/languageFiles/backendFiles")));
const EditLanguageFiles = Loadable(lazy(() => import("./modules/admin/pages/cms/languageFiles/editlanguageFiles")));
const StyleCssFile = Loadable(lazy(() => import("./modules/admin/pages/cms/rootStyles/StyleCssFile")));
const DistributionListTable = Loadable(lazy(() => import("./modules/admin/pages/newsletters/distributionlIst/list")));
const DistributionListView = Loadable(lazy(() => import("./modules/admin/pages/newsletters/distributionlIst/view")));
const DistributionAdd = Loadable(lazy(() => import("./modules/admin/pages/newsletters/distributionlIst/add")));
const DistributionEdit = Loadable(lazy(() => import("./modules/admin/pages/newsletters/distributionlIst/edit")));
const DistributionUsersList = Loadable(lazy(() => import("./modules/admin/pages/newsletters/distributionlIst/usersList")));
const AnnouncementsTable = Loadable(lazy(() => import("./modules/admin/pages/Announcements/list")));
const AnnouncementsAdd = Loadable(lazy(() => import("./modules/admin/pages/Announcements/add")));
const AnnouncementsEdit = Loadable(lazy(() => import("./modules/admin/pages/Announcements/edit")));
const AnnouncementsView = Loadable(lazy(() => import("./modules/admin/pages/Announcements/view")));
const ViewAnnouncement = Loadable(lazy(() => import("./modules/admin/pages/events/viewAnnouncement")));
const LeadManagementTable = Loadable(lazy(() => import("./modules/admin/pages/leadManagement/list")));
const LeadManagementAdd = Loadable(lazy(() => import("./modules/admin/pages/leadManagement/add")));
const LeadManagementEdit = Loadable(lazy(() => import("./modules/admin/pages/leadManagement/edit")));
const LeadManagementView = Loadable(lazy(() => import("./modules/admin/pages/leadManagement/view")));
const LMInvitation = Loadable(lazy(() => import("./modules/admin/pages/events/lmUserInvitation")));
const ViewLMUserInvitationDetails = Loadable(lazy(() => import("./modules/admin/pages/events/lmUserInvitationview")));
const DripContent = Loadable(lazy(() => import("./modules/admin/pages/events/dripcontent")));
const AddDripContent = Loadable(lazy(() => import("./modules/admin/pages/events/addNewDripContent")));
const EventLeads = Loadable(lazy(() => import("./modules/admin/pages/events/leads")));
const LMRegistration = Loadable(lazy(() => import("./modules/admin/pages/events/lmUserRegistartion")));
const ViewLMUserRegistrationDetails = Loadable(lazy(() => import("./modules/admin/pages/events/lmuserRegistrationDetails")));
const SISUserInvitation = Loadable(lazy(() => import("./modules/admin/pages/events/SISInvitation")));
const ViewSISUserInvitationDetails = Loadable(lazy(() => import("./modules/admin/pages/events/SISUserInvitationView")));
const NameBadgeTemplatesAdd = Loadable(lazy(() => import("./modules/admin/pages/cms/nameBadgeTemplate/add")));
const NameBadgeTemplatesEdit = Loadable(lazy(() => import("./modules/admin/pages/cms/nameBadgeTemplate/edit")));
const NameBadgeTemplatesTable = Loadable(lazy(() => import("./modules/admin/pages/cms/nameBadgeTemplate/list")));
const NameBadgeTemplatesView = Loadable(lazy(() => import("./modules/admin/pages/cms/nameBadgeTemplate/view")));
const RoomManagementAdd = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/add")));
const RoomManagementEdit = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/edit")));
const RoomManagementTable = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/list")));
const RoomManagementView = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/view")));
const RoomView = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/viewRoom")));
const BookingTable = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/bookingList")));
const ViewBookingDetails = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/bookingView")));
const EditBooking = Loadable(lazy(() => import("./modules/admin/pages/roomManagement/bookingEdit")));
const BoothAttendantsList = Loadable(lazy(() => import("./modules/admin/pages/events/boothAttendants")));
const SettingView = Loadable(lazy(() => import("./modules/admin/pages/cms/settings/settingView")));
const GlobalPlatformSettings = Loadable(lazy(() => import("./modules/admin/pages/cms/settings/platformSettings")));
const GlobalCommonSettings = Loadable(lazy(() => import("./modules/admin/pages/cms/settings/commonSettings")));
const ViewLinkStatics = Loadable(lazy(() => import("./modules/admin/pages/events/linkStaticsView")));

/** BACK OFFICE ROUTE LAZY IMPORTS */
const BackOfficeDynamicPages = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOfficeDynamicPages")));
const BackOfficeInvitation = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOfficeInvitation")));
const BackOfficeCompleteProfile = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOfficeCompleteProfile")));
const BackOfficeLeadAdd = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/backOfficeLeadAdd")));
const BackOfficeLeadEdit = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/newbackOfficeLeadEdit")));
const BackOfficeViewLead = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/backOfficeLeadView")));
const BackOfficeLeadList = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/backOfficeLeadList")));
const BackOfficeHome = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/BackOfficeHome")));
const BackOfficeProfile = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOfficeProfile")));
const BackOfficeOtpVerification = Loadable(lazy(() => import("./modules/BackOffice/pages/backOfficeOtpVerification")));
const Success = Loadable(lazy(() => import("./modules/globalComponents/Success")));
const Error = Loadable(lazy(() => import("./modules/globalComponents/Error")));
const BackOfficeDraftedLeadsEdit = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/backOfficeDraftedLeadEdit")));
const BackOfficeInvitationList = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/backOfficeInvitationList")));
const BackOfficeSettings = Loadable(lazy(() => import("./modules/BackOffice/pages/backOfficeSetting")));
const BackOfficeChangePassword = Loadable(lazy(() => import("./modules/BackOffice/pages/backOfficeChangePassword")));
const BackOfficeRegistration = Loadable(lazy(() => import("./modules/BackOffice/pages/backOfficeRegistration")));
const BackOfficeRegistrationList = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/backOfficeRegistrationList")));
const BackOfficeDetails = Loadable(lazy(() => import("./modules/BackOffice/pages/BackOffice/backOfficeRgistrationView")));

/** LEAD COORDINATOR (LMUsers) ROUTE LAZY IMPORTS */
const LMUserInvitation = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorInvitation")));
const LMUserCompleteProfile = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorCompleteProfile")));
const LMUserDynamicPages = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorDynamicPages")));
const LeadCoordinatorProfile = Loadable(lazy(() => import("./modules/LMUsers/pages/LeadCoordinatorProfile")));
const AddLead = Loadable(lazy(() => import("./modules/LMUsers/pages/LeadCoordinatorLeadAdd")));
const LEADLIST = Loadable(lazy(() => import("./modules/LMUsers/pages/LeadCoordinatorLeadList")));
const ViewLead = Loadable(lazy(() => import("./modules/LMUsers/pages/LeadCoordinatorLeadView")));
const EditLead = Loadable(lazy(() => import("./modules/LMUsers/pages/LeadCoordinatorLeadEdit")));
const DraftedLeadsEdit = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorDraftLeadEdit")));
const LMUserHome = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorHome")));
const LeadCoordinatorOtpVerification = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorOtpVerification")));
const LeadCoordinatorScanLead = Loadable(lazy(() => import("./modules/LMUsers/pages/LeadCoordinatorScanLead")));
const LeadCoordinatorSettings = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorSetting")));
const LeadCoordinatorChangePassword = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorChangePassword")));
const LeadCoordinatorInvitationList = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorInvitationList")));
const LMUserRegistration = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorRegistration")));
const LeadCoordinatorRegistrationList = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorRegistrationList")));
const LeadCoordinatorDetails = Loadable(lazy(() => import("./modules/LMUsers/pages/leadCoordinatorRegistrationDetails")));

/** SISUSERS ROUTE LAZY IMPORTS */
const SISUserCompleteProfile = Loadable(lazy(() => import("./modules/SISUsers/pages/completeProfile")));
const SISUserDynamicPages = Loadable(lazy(() => import("./modules/SISUsers/pages/dynamicPages")));
const SISUserHome = Loadable(lazy(() => import("./modules/SISUsers/pages/home")));
const SISUserRooms = Loadable(lazy(() => import("./modules/SISUsers/pages/room")));
const SISUserBooking = Loadable(lazy(() => import("./modules/SISUsers/pages/booking")));
const SisUserSettings = Loadable(lazy(() => import("./modules/SISUsers/shared/settings")));
const MyBookings = Loadable(lazy(() => import("./modules/SISUsers/pages/myBooking")));
const SisUserBookingDetail = Loadable(lazy(() => import("./modules/SISUsers/pages/bookingDetail")));
const SisUserBookingEdit = Loadable(lazy(() => import("./modules/SISUsers/pages/editBooking")));
const QrCodeStatus = Loadable(lazy(() => import("./modules/SISUsers/pages/qrCode")));
const ChangeStatus = Loadable(lazy(() => import("./modules/SISUsers/pages/changeStatus")));

/** INFO COUNTER ROUTE LAZY IMPORTS */
const InfoCounterDynamicPages = Loadable(lazy(() => import("./modules/InfoCounter/pages/dynamicPages")));
const InfoCounterHome = Loadable(lazy(() => import("./modules/InfoCounter/pages/home")));
const Table = Loadable(lazy(() => import("./modules/InfoCounter/pages/list")));
const InfoCounterSettings = Loadable(lazy(() => import("./modules/InfoCounter/shared/setting")));
const SISUserAdd = Loadable(lazy(() => import("./modules/InfoCounter/pages/add")));
const EditSISUser = Loadable(lazy(() => import("./modules/InfoCounter/pages/edit")));



const UserShell = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
);

const InfoCounterShell = ({ children }) => (
  <>
    <InfoCounterHeader />
    {children}
    <InfoCounterFooter />
  </>
);

const getRoleBasePath = (role) => {
  if (role === STAFF_ROLE.LM_BACK_OFFICE) return "/back-office";
  if (role === STAFF_ROLE.LEAD_COORDINATOR) return "/lead-coordinator";
  if (role === STAFF_ROLE.BOOTH_ATTENDANTS) return "/sis-user";
  return "";
};

const RoleBasedPage = ({ infoCounterComponent, userComponent, restrictedRoles = [] }) => {
  const currentUser = getUser() || {};
  const isInfoCounter = currentUser?.role === STAFF_ROLE.INFO_COUNTER;
  const isUser = [STAFF_ROLE.USER, STAFF_ROLE.STAFF, STAFF_ROLE.ADMIN, STAFF_ROLE.SUB_ADMIN].includes(currentUser?.role);
  
  if (!isInfoCounter && !isUser) {
    const basePath = getRoleBasePath(currentUser?.role);
    return <Navigate to={basePath ? `${basePath}/home` : "/login"} replace />;
  }
  
  if (isInfoCounter) {
    return (
      <InfoCounterShell>
        <InfoCounterPrivateRoute component={infoCounterComponent} />
      </InfoCounterShell>
    );
  }

  return (
    <UserShell>
      <UserPrivateRoute component={userComponent} restrictedRoles={restrictedRoles} />
    </UserShell>
  );
};

const RoleBasedOrgInfo = () => {
  const currentUser = getUser() || {};
  const isInfoCounter = currentUser?.role === STAFF_ROLE.INFO_COUNTER;
  const isUser = [STAFF_ROLE.USER, STAFF_ROLE.STAFF, STAFF_ROLE.ADMIN, STAFF_ROLE.SUB_ADMIN].includes(currentUser?.role);

  if (!isInfoCounter && !isUser) {
    const basePath = getRoleBasePath(currentUser?.role);
    return <Navigate to={basePath ? `${basePath}/home` : "/login"} replace />;
  }

  return isInfoCounter ? (
    <InfoCounterShell>
      <InfoCounterDynamicPages />
    </InfoCounterShell>
  ) : (
    <UserShell>
      <DynamicPages />
    </UserShell>
  );
};

const RoleBasedLeaf = ({ infoCounterComponent, userComponent, restrictedRoles = [] }) => {
  const currentUser = getUser() || {};
  const isInfoCounter = currentUser?.role === STAFF_ROLE.INFO_COUNTER;
  const isUser = [STAFF_ROLE.USER, STAFF_ROLE.STAFF, STAFF_ROLE.ADMIN, STAFF_ROLE.SUB_ADMIN].includes(currentUser?.role);

  if (!isInfoCounter && !isUser) {
    const basePath = getRoleBasePath(currentUser?.role);
    return <Navigate to={basePath ? `${basePath}/home` : "/login"} replace />;
  }

  if (isInfoCounter) {
    return <InfoCounterPrivateRoute component={infoCounterComponent} />;
  }

  return <UserPrivateRoute component={userComponent} restrictedRoles={restrictedRoles} />;
};

const InfoCounterOnlyPage = ({ component }) => {
  const currentUser = getUser() || {};
  const isInfoCounter = currentUser?.role === STAFF_ROLE.INFO_COUNTER;

  if (!isInfoCounter) {
    const basePath = getRoleBasePath(currentUser?.role);
    return <Navigate to={basePath ? `${basePath}/home` : "/login"} replace />;
  }

  return (
    <InfoCounterShell>
      <InfoCounterPrivateRoute component={component} />
    </InfoCounterShell>
  );
};

const LegacyInfoCounterRedirect = () => {
  const params = useParams();
  const legacyPath = params?.["*"] || "";
  return <Navigate to={legacyPath ? `/${legacyPath}` : "/home"} replace />;
};

const router = createBrowserRouter([

  {
    path: "/test-pages",
    element: <NewTestPages />,
  },
  {
    path: "/info-counter/*",
    element: <LegacyInfoCounterRedirect />,
  },
  {
    path: "/",
    element: <FrontRoot />,
    children: [
      { path: "", element: <RoleBasedPage infoCounterComponent={InfoCounterHome} userComponent={Home} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE, STAFF_ROLE.LEAD_COORDINATOR]} /> },
      { path: "home", element: <RoleBasedPage infoCounterComponent={InfoCounterHome} userComponent={Home} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE, STAFF_ROLE.LEAD_COORDINATOR]} /> },
      { path: "org-info/:slug", element: <RoleBasedOrgInfo /> },
      { path: "sis-user/list/:pgno", element: <InfoCounterOnlyPage component={Table} /> },
      { path: "sis-user/add", element: <InfoCounterOnlyPage component={SISUserAdd} /> },
      { path: "sis-user/edit/:id", element: <InfoCounterOnlyPage component={EditSISUser} /> },
      { path: "announcements/list/:pgno", element: <RoleBasedPage infoCounterComponent={UserAnnouncements} userComponent={UserAnnouncements} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} /> },
      { path: "announcements/details/:id", element: <RoleBasedPage infoCounterComponent={UserViewAnnouncements} userComponent={UserViewAnnouncements} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} /> },
      { path: "notifications/:pgno", element: <RoleBasedPage infoCounterComponent={UserNotifications} userComponent={UserNotifications} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} /> },
      {
        path: "settings",
        element: <RoleBasedPage infoCounterComponent={InfoCounterSettings} userComponent={Settings} />,
        children: [
          { path: "", element: <RoleBasedLeaf infoCounterComponent={UserProfile} userComponent={UserProfile} /> },
          { path: "profile", element: <RoleBasedLeaf infoCounterComponent={UserProfile} userComponent={UserProfile} /> },
          { path: "change-password", element: <RoleBasedLeaf infoCounterComponent={UserChangePassword} userComponent={UserChangePassword} /> },
          { path: "event-registrations/list/:pgno", element: <RoleBasedLeaf infoCounterComponent={EventRegistrationList} userComponent={EventRegistrationList} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} /> },
          { path: "event-registrations/view/:id", element: <RoleBasedLeaf infoCounterComponent={EventRegistrationView} userComponent={EventRegistrationView} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} /> },
          { path: "invitation-list/:pgno", element: <RoleBasedLeaf infoCounterComponent={InvitationList} userComponent={InvitationList} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} /> },
          { path: "transaction-list/:pgno", element: <RoleBasedLeaf infoCounterComponent={TransactionList} userComponent={TransactionList} restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} /> },
        ],
      },
    ],
  },
  /** USER ROUTE START */
  {
    path: "/",
    element: <FrontRoot />,
    children: [
      {
        path: "/",
        element: <Userlayout />,

        children: [
          {
            path: "",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE, STAFF_ROLE.LEAD_COORDINATOR]} component={Home} />,
          },
          {
            path: "org-info/:slug",
            element: <DynamicPages />,
          },
          {
            path: "dynamic-pages/:slug",
            element: <DynamicPages />,
          },
          {
            path: "settings",
            element: <UserPrivateRoute component={Settings} />,
            children: [
              {
                path: "",
                element: <UserPrivateRoute component={UserProfile} />,
              },
              {
                path: "profile",
                element: <UserPrivateRoute component={UserProfile} />,
              },
              {
                path: "change-password",
                element: <UserPrivateRoute component={UserChangePassword} />,
              },
              {
                path: "event-registrations/list/:pgno",
                element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={EventRegistrationList} />,
              },
              {
                path: "event-registrations/view/:id",
                element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={EventRegistrationView} />,
              },
              {
                path: "shared-files/:pgno",
                element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={SharedFiles} />,
              },
              {
                path: "invitation-list/:pgno",
                element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={InvitationList} />,
              },
              {
                path: "transaction-list/:pgno",
                element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={TransactionList} />,
              },
               {
                path: "booking-list/:pgno",
                element: <UserPrivateRoute component={StaffMyBookings} />,
              },
              {
                path: "booking/details/:id",
                element: <UserPrivateRoute component={ViewStaffBooking} />
              },
              {
                path: "booking/edit/:id",
                element: <UserPrivateRoute component={StaffEditBooking} />
              },
              {
                path: "setting",
                element: <UserPrivateRoute component={UserSetting} />
              },
            ]
          },
          {
            path: "activity-forum/:pgno",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={UserActivity} />
          },
          {
            path: "activity-forum/topic/:id",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={UserTopics} />,
          },
          {
            path: "activity-forum/topicView/:id",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={UserTopicsView} />,
          },
          {
            path: "survey/:pgno",
            element: (
              <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={SurveyTable} />
            ),
          },
          {
            path: "survey/question/:id",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={SurveyQuestions} />,
          },
          {
            path: "survey/thanks",
            element: <UserPrivateRoute component={SurveyThanks} />,
          },
          {
            path: "event-detail/:id",
            element: <UserPrivateRoute component={EventDetailsPage} />,
          },
          {
            path: "event-preview/:slug",
            element: <EventDetailsPreviewAdmin />,
          },
          {
            path: "event-registration",
            element: <UserPrivateRoute component={Registration} />,
          },
          {
            path: "registration-thanks-page",
            element: <UserPrivateRoute component={RegistrationThanksPage} />,
          },
          {
            path: "invoice-payment-page",
            element: <UserPrivateRoute component={InvoicePaymentPage} />,
          },
          {
            path: "invitation-reject-page",
            element: <UserPrivateRoute component={InvitationRejectPage} />,
          },
          {
            path: "event-registration-step-2",
            element: <UserPrivateRoute component={RegistrationStepTwo} />,
          },
          {
            path: "announcements/list/:pgno",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={UserAnnouncements} />
          },
          {
            path: "announcements/details/:id",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={UserViewAnnouncements} />
          },
          {
            path: "notifications/:pgno",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={UserNotifications} />
          },
          {
            path: "open-registration/events/list/:pgno",
            element: <UserEvents />
          },
          // {
          //   path: "open-registration/event/details/:id",
          //   element: <OpenRegistrationEventDetailsPage/>,
          // },
          {
            path: "open-registration/event/details/:id",
            element: <EventDetailsPage />,
          },
          {
            path: "rooms/list/:pgno",
            element: <UserPrivateRoute component={StaffRooms} />,
          },
          {
            path: "rooms/book/:id",
            element: <UserPrivateRoute component={StaffUserBooking} />
          },
          {
            path: "faq",
            element: <FAQ />,
          },
          {
            path: "privacy-policy",
            element: <PrivacyPolicy />,
          },
          {
            path: "users/:pgno",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={Join} />,
          },
          {
            path: "chat",
            element: <UserPrivateRoute restrictedRoles={[STAFF_ROLE.LM_BACK_OFFICE]} component={Chat} />,
          },
          {
            path: "terms-condition",
            element: <TermsCondition />,
          },
          {
            path: "/login",
            element: <UserLogin />
          },
          {
            path: "/complete-profile",
            element: <UserPublicRoute component={CompleteProfile} />
          },
          {
            path: "/forget-password",
            element: <UserPublicRoute component={UserForgetPassword} />,
          },
          {
            path: "/otp-verificaiton",
            element: <UserOtpVerification />,
          },
          {
            path: "/reset-password",
            element: <UserPublicRoute component={ResetPassword} />,
          },
          { path: "/404", element: <Error404User /> },
          { path: "*", element: <Error404User /> },
        ],
      },
    ],
    /* For User section */
  },
  /**USER ROUTE END */

  /**LEAD COORDINATOR ROUTE START */
  {
    path: "/",
    element: <FrontRoot />,
    children: [
      {
        path: "/lead-coordinator",
        element: <LMUserslayout />,

        children: [
          {
            path: "home",
            element: <LMUsersPrivateRoute component={LMUserHome} />,
          },
          {
            path: "org-info/:slug",
            element: <DynamicPages />,
          },
          {
            path: "invitation",
            element: <LMUserInvitation />,
          },
          // {
          //   path: "profile",
          //   element: <LMUsersPrivateRoute component={LeadCoordinatorProfile} />,
          // },
          {
            path: "otp-verificaiton",
            element: <LeadCoordinatorOtpVerification />,
          },
          {
            path: "leads/list/:pgno",
            element: <LMUsersPrivateRoute component={LEADLIST} />
          },
          {
            path: "leads/add",
            element: <LMUsersPrivateRoute component={AddLead} />
          },
          {
            path: "leads/scan",
            element: <LMUsersPrivateRoute component={LeadCoordinatorScanLead} />
          },
          {
            path: "leads/details/:id",
            element: <LMUsersPrivateRoute component={ViewLead} />
          },
          {
            path: "leads/edit/:id",
            element: <LMUsersPrivateRoute component={EditLead} />
          },
          // {
          //   path: "draft-customers/list/:pgno",
          //   element: <LMUsersPrivateRoute  component={DraftedLeadsList} />
          // },
          {
            path: "draft-customers/edit/:id",
            element: <LMUsersPrivateRoute component={DraftedLeadsEdit} />
          },
          {
            path: "settings",
            element: <LMUsersPrivateRoute component={LeadCoordinatorSettings} />,
            children: [
              {
                path: "",
                element: <LMUsersPrivateRoute component={LeadCoordinatorProfile} />,
              },
              {
                path: "profile",
                element: <LMUsersPrivateRoute component={LeadCoordinatorProfile} />,
              },
              {
                path: "change-password",
                element: <LMUsersPrivateRoute component={LeadCoordinatorChangePassword} />,
              },
              {
                path: "invitation-list/:pgno",
                element: <UserPrivateRoute component={LeadCoordinatorInvitationList} />,
              },
              {
                path: "registration-list/:pgno",
                element: <UserPrivateRoute component={LeadCoordinatorRegistrationList} />,
              },
              {
                path: "registration-details/:id",
                element: <UserPrivateRoute component={LeadCoordinatorDetails} />,
              },
            ]
          },

          { path: "*", element: <Error404User /> },
        ],
      },
      {
        path: "lead-coordinator/complete-profile",
        element: <LMUserCompleteProfile />,
      },
      {
        path: "lead-coordinator/registration",
        element: <LMUserRegistration />,
      },
    ],
  },
  /** LEAD COORDINATOR ROUTE END */


  /**BACK OFFICE ROUTE START */
  {
    path: "/",
    element: <FrontRoot />,
    children: [
      {
        path: "/back-office",
        element: <BackOfficeLayout />,
        children: [
          {
            path: "home",
            element: <BackOfficePrivateRoute component={BackOfficeHome} />,
          },
          {
            path: "org-info/:slug",
            element: <DynamicPages />,
          },
          {
            path: "invitation",
            element: <BackOfficeInvitation />,
          },

          // {
          //   path: "profile",
          //   element: <BackOfficePrivateRoute component={BackOfficeProfile} />,
          // },
          {
            path: "otp-verificaiton",
            element: <BackOfficeOtpVerification />,
          },
          {
            path: "leads/list/:pgno",
            element: <BackOfficePrivateRoute component={BackOfficeLeadList} />
          },
          {
            path: "leads/add",
            element: <BackOfficePrivateRoute component={BackOfficeLeadAdd} />
          },
          {
            path: "leads/details/:id",
            element: <BackOfficePrivateRoute component={BackOfficeViewLead} />
          },
          {
            path: "leads/edit/:id",
            element: <BackOfficePrivateRoute component={BackOfficeLeadEdit} />
          },
          // {
          //   path: "draft-customers/list/:pgno",
          //   element: <BackOfficePrivateRoute  component={BackOfficeDraftedLeadsList} />
          // },
          {
            path: "draft-customers/edit/:id",
            element: <BackOfficePrivateRoute component={BackOfficeDraftedLeadsEdit} />
          },
          {
            path: "settings",
            element: <BackOfficePrivateRoute component={BackOfficeSettings} />,
            children: [
              {
                path: "",
                element: <BackOfficePrivateRoute component={BackOfficeProfile} />,
              },
              {
                path: "profile",
                element: <BackOfficePrivateRoute component={BackOfficeProfile} />,
              },
              {
                path: "change-password",
                element: <BackOfficePrivateRoute component={BackOfficeChangePassword} />,
              },
              {
                path: "invitation-list/:pgno",
                element: <UserPrivateRoute component={BackOfficeInvitationList} />,
              },
              {
                path: "registration-list/:pgno",
                element: <UserPrivateRoute component={BackOfficeRegistrationList} />,
              },
              {
                path: "registration-details/:id",
                element: <UserPrivateRoute component={BackOfficeDetails} />,
              },
            ]
          },
          { path: "*", element: <Error404User /> },
        ],
      },
      {
        path: "success",
        element: <Success />,
      },
      {
        path: "error",
        element: <Error />,
      },
      {
        path: "back-office/complete-profile",
        element: <BackOfficeCompleteProfile />,
      },
      {
        path: "back-office/registration",
        element: <BackOfficeRegistration />,
      },
    ],
  },
  /**BACK OFFICE ROUTE END */

  /** SIS USER ROUTE START */
  {
    path: "/",
    element: <FrontRoot />,
    children: [
      {
        path: "/sis-user",
        element: <SISUserLayout />,
        children: [
          {
            path: "home",
            element: <SISUserPrivateRoute component={SISUserHome} />,
          },
          {
            path: "org-info/:slug",
            element: <DynamicPages />,
          },
          {
            path: "rooms/list/:pgno",
            element: <SISUserPrivateRoute component={SISUserRooms} />
          },
          {
            path: "rooms/book/:id",
            element: <SISUserPrivateRoute component={SISUserBooking} />
          },
          {
            path: "announcements/list/:pgno",
            element: <SISUserPrivateRoute component={UserAnnouncements} />
          },
          {
            path: "announcements/details/:id",
            element: <SISUserPrivateRoute component={UserViewAnnouncements} />
          },
          {
            path: "notifications/:pgno",
            element: <SISUserPrivateRoute component={UserNotifications} />
          },
          {
            path: "settings",
            element: <SISUserPrivateRoute component={SisUserSettings} />,
            children: [
              {
                path: "",
                element: <SISUserPrivateRoute component={UserProfile} />,
              },
              {
                path: "profile",
                element: <SISUserPrivateRoute component={UserProfile} />,
              },
              {
                path: "change-password",
                element: <SISUserPrivateRoute component={UserChangePassword} />,
              },
              {
                path: "event-registrations/list/:pgno",
                element: <SISUserPrivateRoute component={EventRegistrationList} />,
              },
              {
                path: "event-registrations/view/:id",
                element: <SISUserPrivateRoute component={EventRegistrationView} />,
              },
              {
                path: "invitation-list/:pgno",
                element: <SISUserPrivateRoute component={InvitationList} />,
              },
              {
                path: "transaction-list/:pgno",
                element: <SISUserPrivateRoute component={TransactionList} />,
              },
              {
                path: "booking-list/:pgno",
                element: <SISUserPrivateRoute component={MyBookings} />,
              },
              {
                path: "booking/details/:id",
                element: <SISUserPrivateRoute component={SisUserBookingDetail} />
              },
              {
                path: "booking/edit/:id",
                element: <SISUserPrivateRoute component={SisUserBookingEdit} />
              },
              {
                path: "status-for-qr-code",
                element: <SISUserPrivateRoute component={QrCodeStatus} />
              },
              {
                path: "change-status",
                element: <SISUserPrivateRoute component={ChangeStatus} />
              },
              {
                path: "setting",
                element: <SISUserPrivateRoute component={UserSetting} />
              },
            ]
          },
          { path: "*", element: <Error404User /> },
        ],
      },
      {
        path: "sis-user/complete-profile",
        element: <SISUserCompleteProfile />,
      },
    ],
  },
  /** SIS USER ROUTE END */

  /** INFO COUNTER ROUTE START */
  {
    path: "/",
    element: <FrontRoot />,
    children: [
      {
        path: "/info-counter",
        element: <InfoCounterLayout />,
        children: [
          {
            path: "home",
            element: <InfoCounterPrivateRoute component={InfoCounterHome} />,
          },
          {
            path: "org-info/:slug",
            element: <DynamicPages />,
          },
          {
            path: "sis-user/list/:pgno",
            element: <InfoCounterPrivateRoute component={Table} />
          },
          {
            path: "sis-user/add",
            element: <InfoCounterPrivateRoute component={SISUserAdd} />
          },
          {
            path: "sis-user/edit/:id",
            element: <InfoCounterPrivateRoute component={EditSISUser} />
          },
          {
            path: "announcements/list/:pgno",
            element: <InfoCounterPrivateRoute component={UserAnnouncements} />
          },
          {
            path: "announcements/details/:id",
            element: <InfoCounterPrivateRoute component={UserViewAnnouncements} />
          },
          {
            path: "notifications/:pgno",
            element: <InfoCounterPrivateRoute component={UserNotifications} />
          },
          {
            path: "settings",
            element: <InfoCounterPrivateRoute component={InfoCounterSettings} />,
            children: [
              {
                path: "",
                element: <InfoCounterPrivateRoute component={UserProfile} />,
              },
              {
                path: "profile",
                element: <InfoCounterPrivateRoute component={UserProfile} />,
              },
              {
                path: "change-password",
                element: <InfoCounterPrivateRoute component={UserChangePassword} />,
              },
              {
                path: "event-registrations/list/:pgno",
                element: <InfoCounterPrivateRoute component={EventRegistrationList} />,
              },
              {
                path: "event-registrations/view/:id",
                element: <InfoCounterPrivateRoute component={EventRegistrationView} />,
              },
              {
                path: "invitation-list/:pgno",
                element: <InfoCounterPrivateRoute component={InvitationList} />,
              },
              {
                path: "transaction-list/:pgno",
                element: <InfoCounterPrivateRoute component={TransactionList} />,
              },
              // {
              //   path: "booking-list/:pgno",
              //   element: <InfoCounterPrivateRoute component={MyBookings} />,
              // },
              // {
              //   path: "booking/details/:id",
              //   element: <InfoCounterPrivateRoute component={SisUserBookingDetail} />
              // },
              // {
              //   path: "booking/edit/:id",
              //   element: <InfoCounterPrivateRoute component={SisUserBookingEdit} />
              // },
            ]
          },
          { path: "*", element: <Error404User /> },
        ],
      },
      // {
      //   path: "sis-user/complete-profile",
      //   element: <SISUserCompleteProfile />,
      // },
    ],
  },
  /** INFO COUNTER ROUTE END */

  /**ADMIN ROUTE START */
  {
    path: "admin",
    element: <Adminlayout />,
    children: [
      {
        path: "",
        element: <Navigate to={"/admin/dashboard"} />,
      },
      {
        path: "dashboard",
        element: <PrivateRoute component={Dashboard} route={'/admin/dashboard'} />,
      },
      {
        path: "invoice-design",
        element: <PrivateRoute route={"/admin/invoice-design"} component={InvoiceDesign} />,
      },
      {
        path: "profile",
        element: <PrivateRoute component={Profile} route={'/admin/profile'} />,
        children: [
          {
            path: "edit/:id",
            element: <PrivateRoute component={EditProfile} route={'/admin/profile/edit/*'} />,
          },
          {
            path: "change-password/:id",
            element: <PrivateRoute component={Changepassword} route={'/admin/profile/change-password/*'} />,
          },
        ],
      },

      {/**user management route start */ },
      {
        path: "user-management/users/list/:pgno",
        element: <PrivateRoute component={UsersTable} route={'/admin/user-management/users/list/*'} />,
      },
      {
        path: "user-management/users/view/:id",
        element: <PrivateRoute component={UsersView} route={'/admin/user-management/users/view/*'} />,
      },
      {
        path: "user-management/users/add",
        element: <PrivateRoute component={UsersAdd} route={'/admin/user-management/users/add'} />,
      },
      {
        path: "user-management/users/:pgno/edit/:id",
        element: <PrivateRoute component={UserEdit} route={'/admin/user-management/users/*/edit/*'} />,
      },
      {/**user management route end */ },


      {/**staff route start */ },
      {
        path: "staff/list/:pgno",
        element: <PrivateRoute component={StaffTable} route={'/admin/staff/list/*'} />,
      },
      {
        path: "staff/add",
        element: <PrivateRoute component={StaffAdd} route={'/admin/staff/add'} />,
      },
      {
        path: "staff/:pgno/edit/:id",
        element: <PrivateRoute component={StaffsEdit} route={'/admin/staff/*/edit/*'} />,
      },
      {
        path: "staff/view/:id",
        element: <PrivateRoute component={StaffView} route={'/admin/staff/view/*'} />,
      },
      {/**staff route end */ },

      {/**lead management route start */ },
      {
        path: "lead-management/list/:pgno",
        element: <PrivateRoute component={LeadManagementTable} route={'/admin/staff/list/*'} />,
      },
      {
        path: "lead-management/add",
        element: <PrivateRoute component={LeadManagementAdd} route={'/admin/staff/add'} />,
      },
      {
        path: "lead-management/:pgno/edit/:id",
        element: <PrivateRoute component={LeadManagementEdit} route={'/admin/staff/*/edit/*'} />,
      },
      {
        path: "lead-management/view/:id",
        element: <PrivateRoute component={LeadManagementView} route={'/admin/staff/view/*'} />,
      },
      {/**lead management route end */ },



      {/**Event management route start */ },
      {
        path: "event-management/events/list/:pgno",
        element: <PrivateRoute component={EventsTable} route={'/admin/event-management/events/list/*'} />,
      },
      {
        path: "event-management/events/add",
        element: <PrivateRoute component={EventsAdd} route={'/admin/event-management/events/add'} />,
        children: [
          {
            path: "",
            element: <PrivateRoute component={Event} route={'/admin/event-management/events/add'} />,
          },
          {
            path: ":id",
            element: <PrivateRoute component={Event} route={'/admin/event-management/events/add/*'} />,
          },
          {
            path: "hotels/:id",
            element: <PrivateRoute component={Hotels} route={'/admin/event-management/events/add/hotels/*'} />,
          },
          {
            path: "agenda/:id",
            element: <PrivateRoute component={Agenda} route={'/admin/event-management/events/add/agenda/*'} />,
          },
        ],
      },
      {
        path: "event-management/events/edit",
        element: <PrivateRoute component={EventsEdit} route={'/admin/event-management/events/edit'} />,
        children: [
          {
            path: ":id",
            element: <PrivateRoute component={Event} route={'/admin/event-management/events/edit/*'} />,
          },
          {
            path: "hotels/:id/:pgno?",
            element: <PrivateRoute component={Hotels} route={'/admin/event-management/events/edit/hotels/*/*'} />,
          },
          {
            path: "agenda/:id/:pgno?",
            element: <PrivateRoute component={Agenda} route={'/admin/event-management/events/edit/agenda/*/*'} />,
          },
        ],
      },
      {
        path: "event-management/events/view",
        element: <PrivateRoute component={View} route={'/admin/event-management/events/view'} />,
        children: [
          {
            path: "event/:id",
            element: <PrivateRoute component={ViewEvent} route={'/admin/event-management/events/view/event/*'} />,
          },
          {
            path: "hotels/:id/:pgno",
            element: <PrivateRoute component={ViewHotels} route={'/admin/event-management/events/view/hotels/*/*'} />,
          },
          {
            path: "agenda/:id/:pgno",
            element: <PrivateRoute component={ViewAgenda} route={'/admin/event-management/events/view/agenda/*/*'} />,
          },
          {
            path: "registrations/:id/:pgno",
            element: <PrivateRoute component={ViewRegistrations} route={'/admin/event-management/events/view/registrations/*/*'} />,
          },
          {
            path: "invitations/:id",
            element: <PrivateRoute component={ViewInvitations} route={'/admin/event-management/events/view/invitations/*'} />,
          },
          {
            path: "event-transactions/:id/:pgno",
            element: <PrivateRoute component={EventTransactions} route={'/admin/event-management/events/view/event-transactions/*/*'} />,
          },
          {
            path: "attendees/:id/:pgno",
            element: <PrivateRoute component={ViewAttendees} route={'/admin/event-management/events/view/attendees/*/*'} />,
          },
          {
            path: "event-announcements/:id/:pgno",
            element: <PrivateRoute component={ViewAnnouncement} route={'/admin/event-management/events/view/event-announcements/*/*'} />,
          },
          {
            path: "checkIn-checkOut/:id/:pgno",
            element: <PrivateRoute component={ViewTicketDetails} route={'/admin/event-management/events/view/checkIn-checkOut/*/*'} />,
          },
          {
            path: "event-lm-user-invitation/:id/:pgno",
            element: <PrivateRoute component={LMInvitation} route={'/admin/event-management/events/view/event-announcements/*/*'} />,
          },
          {
            path: "event-lm-user-registration-list/:id/:pgno",
            element: <PrivateRoute component={LMRegistration} route={'/admin/event-management/events/view/event-announcements/*/*'} />,
          },
          {
            path: "event-drip-content/:id",
            element: <PrivateRoute component={DripContent} route={'/admin/event-management/events/view/event-announcements/*/*'} />,
          },
          {
            path: "event-leads/:id/:pgno",
            element: <PrivateRoute component={EventLeads} route={'/admin/event-management/events/view/event-announcements/*/*'} />,
          },
          {
            path: "event-sis-user-invitation/:id/:pgno",
            element: <PrivateRoute component={SISUserInvitation} route={'/admin/event-management/events/view/event-announcements/*/*'} />,
          },
          {
            path: "event-booth-attendants/:id/:pgno",
            element: <PrivateRoute component={BoothAttendantsList} route={'/admin/event-management/events/view/event-announcements/*/*'} />,
          },
        ],
      },
      {
        path: "event-management/events/view/:registrationId",
        element: <PrivateRoute component={ViewRegistrationDetails} route={'/admin/event-management/events/view/*'} />,
      },
      {
        path: "event-management/events/view/invoice/:registrationId",
        element: <PrivateRoute component={ViewInvoiceDetails} route={'/admin/event-management/events/view/invoice/*'} />,
      },
      {
        path: "event-management/events/registration-edit/:registrationId",
        element: <PrivateRoute component={EditRegistrationDetail} route={'/admin/event-management/events/registration-edit/*'} />,
      },
      {
        path: "event-management/events/view/attendees/:attendeesId",
        element: <PrivateRoute component={ViewAttendeesDetails} route={'/admin/event-management/events/attendees/*'} />,
      },
      {
        path: "event-management/events/view/lm-user-invitation-details/:LMUserInvitationId",
        element: <PrivateRoute component={ViewLMUserInvitationDetails} route={'/admin/event-management/events/attendees/*'} />,
      },
      {
        path: "event-management/events/view/lm-user-registration-details/:LMUserRegistrationId",
        element: <PrivateRoute component={ViewLMUserRegistrationDetails} route={'/admin/event-management/events/attendees/*'} />,
      },
      {
        path: "event-management/events/view/link-statics-details/:pgno",
        element: <PrivateRoute component={ViewLinkStatics} route={'/admin/event-management/events/attendees/*'} />,
      },
      {
        path: "event-management/events/view/drip-content-add",
        element: <PrivateRoute component={AddDripContent} route={'/admin/event-management/events/attendees/*'} />,
      },
      {
        path: "event-management/events/registration-edit-step-two",
        element: <PrivateRoute component={EditRegistrationDetailStepTwo} route={'/admin/event-management/events/registration-edit-step-two'} />,
      },
      {
        path: "event-management/events/view/sis-user-invitation-details/:SISUserInvitationId",
        element: <PrivateRoute component={ViewSISUserInvitationDetails} route={'/admin/event-management/events/attendees/*'} />,
      },
      {
        path: "event-management/tickets/list/:pgno",
        element: <PrivateRoute component={TicketTable} />,
      },
      {
        path: "event-management/tickets/add",
        element: <PrivateRoute component={TicketAdd} />,
      },
      {
        path: "event-management/tickets/:pgno/edit/:id",
        element: <PrivateRoute component={TicketEdit} />,
      },
      {
        path: "event-management/tickets/view/:id",
        element: <PrivateRoute component={TicketView} />,
      },
      {
        path: "event-management/invitations/list/:pgno",
        element: <PrivateRoute component={InvitationsList} />,
      },
      {
        path: "event-management/invitations/view/:id",
        element: <PrivateRoute component={InvitationView} />,
      },
      {
        /**Event Management Route End */
      },

      {
        /**Cms Route Start */
      },
      {
        path: "cms/pages/list/:pgno",
        element: <PrivateRoute component={PageTable} route={'/admin/cms/pages/list/*'} />,
      },
      {
        path: "cms/pages/add",
        element: <PrivateRoute component={PageAdd} route={'/admin/cms/pages/add'} />
      },
      {
        path: "cms/pages/:pgno/edit/:id",
        element: <PrivateRoute component={PageEdit} route={'/admin/cms/pages/*/edit/*'} />,
      },
      {
        path: "cms/pages/view/:id",
        element: <PrivateRoute component={PageView} route={'/admin/cms/pages/view/*'} />,
      },

      {
        path: "cms/banner/list/:pgno",
        element: <PrivateRoute component={BannerTable} route={'/admin/cms/banner/list/*'} />,
      },
      {
        path: "cms/banner/add",
        element: <PrivateRoute component={BannerAdd} route={'/admin/cms/banner/add'} />,
      },
      {
        path: "cms/banner/:pgno/edit/:id",
        element: <PrivateRoute component={BannerEdit} route={'/admin/cms/banner/*/edit/*'} />,
      },
      {
        path: "cms/banner/view/:id",
        element: <PrivateRoute component={BannerView} route={'/admin/cms/banner/view/*'} />,
      },

      // {
      //   path: "cms/settings",
      //   element: <PrivateRoute component={GlobalSettings} route={'/admin/cms/settings'} />,
      // },
      {
        path: "cms/settings",
        element: <PrivateRoute component={SettingView}  route={'/admin/cms/settings'} />,
        children: [
          {
            path: "general-settings",
            element: <PrivateRoute component={GlobalSettings} route={'/admin/cms/settings'}  />,
          },
          {
            path: "platform-settings",
            element: <PrivateRoute component={GlobalPlatformSettings} route={'/admin/cms/settings'}  />,
          },
          {
            path: "common-settings", 
            element: <PrivateRoute component={GlobalCommonSettings} route={'/admin/cms/settings'} />,
          },
        ],
      },
      {
        path: "cms/language-files",
        element: <PrivateRoute component={LanguageFiles} route={'/admin/cms/language-files'} />,
        children: [
          {
            path: "frontend",
            element: <PrivateRoute component={FrontendFiles} route={'/admin/cms/language-files/frontend'} />,
          },
          {
            path: "backend",
            element: <PrivateRoute component={BackendFiles} route={'/admin/cms/language-files/backend'} />,
          },
          {
            path: "edit",
            element: <PrivateRoute component={EditLanguageFiles} route={'/admin/cms/language-files/edit'} />,
          },
        ],
      },
      {
        path: "cms/style-files",
        element: <PrivateRoute component={StyleCssFile} route={'/admin/cms/style-files'} />,
      },
      {
        path: "cms/portal-design",
        element: <PrivateRoute component={PortalDesign} route={'/admin/cms/portal-design'} />,
      },

      {
        path: "cms/default-email-template/list/:pgno",
        element: <PrivateRoute component={EmailTemplatesTable} route={'/admin/cms/default-email-template/list/*'} />,
      },
      {
        path: "cms/default-email-template/add",
        element: <PrivateRoute component={EmailTempAdd} route={'/admin/cms/default-email-template/add'} />,
      },
      {
        path: "cms/default-email-template/:pgno/edit/:id",
        element: <PrivateRoute component={EmailTempEdit} route={'/admin/cms/default-email-template/*/edit/*'} />,
      },
      {
        path: "cms/default-email-template/view/:id",
        element: <PrivateRoute component={ViewEmailTemp} route={'/admin/cms/default-email-template/view/*'} />,
      },

      {
        path: "cms/push-notification-template/list/:pgno",
        element: <PrivateRoute component={PushNotificationTable} route={'/admin/cms/push-notification-template/list/*'} />,
      },
      {
        path: "cms/push-notification-template/add",
        element: <PrivateRoute component={PushNotificationAdd} route={'/admin/cms/push-notification-template/add'} />,
      },
      {
        path: "cms/push-notification-template/:pgno/edit/:id",
        element: <PrivateRoute component={PushNotificationEdit} route={'/admin/cms/push-notification-template/*/edit/*'} />,
      },
      {
        path: "cms/push-notification-template/view/:id",
        element: <PrivateRoute component={ViewPushNotification} route={'/admin/cms/push-notification-template/view/*'} />,
      },

      {
        path: "cms/promotional-email-templates/list/:pgno",
        element: <PrivateRoute component={EmailTemplatesTableNews} route={'/admin/cms/promotional-email-templates/list/*'} />,
      },
      {
        path: "cms/promotional-email-templates/add",
        element: <PrivateRoute component={EmailTempAddNews} route={'/admin/cms/promotional-email-templates/add'} />,
      },
      {
        path: "cms/promotional-email-templates/:pgno/edit/:id",
        element: <PrivateRoute component={EmailTempEditNews} route={'/admin/cms/promotional-email-templates/*/edit/*'} />,
      },
      {
        path: "cms/promotional-email-templates/view/:id",
        element: <PrivateRoute component={ViewEmailTempNews} route={'/admin/cms/promotional-email-templates/view/*'} />,
      },
      {
        path: "cms/name-badge-templates/list/:pgno",
        element: <PrivateRoute component={NameBadgeTemplatesTable} route={'/admin/cms/promotional-email-templates/list/*'} />,
      },
      {
        path: "cms/name-badge-templates/add",
        element: <PrivateRoute component={NameBadgeTemplatesAdd} route={'/admin/cms/promotional-email-templates/add'} />,
      },
      {
        path: "cms/name-badge-templates/:pgno/edit/:id",
        element: <PrivateRoute component={NameBadgeTemplatesEdit} route={'/admin/cms/promotional-email-templates/*/edit/*'} />,
      },
      {
        path: "cms/name-badge-templates/view/:id",
        element: <PrivateRoute component={NameBadgeTemplatesView} route={'/admin/cms/promotional-email-templates/view/*'} />,
      },
      {
        /* cms route end */
      },

      {
        /**Question Route start */
      },
      {
        path: "question-settings/sets/list/:pgno",
        element: <PrivateRoute component={QuestionSetTable} route={'/admin/question-settings/sets/list/*'} />,
      },
      {
        path: "question-settings/sets/add",
        element: <PrivateRoute component={QuestionSetAdd} route={'/admin/question-settings/sets/add'} />,
      },
      {
        path: "question-settings/sets/:pgno/edit/:id",
        element: <PrivateRoute component={QuestionSetEdit} route={'/admin/question-settings/sets/*/edit/*'} />,
      },
      {
        path: "question-settings/sets/view/:id",
        element: <PrivateRoute component={QuestionSetView} route={'/admin/question-settings/sets/view/*'} />,
      },
      {
        path: "question-settings/questions/add",
        element: <PrivateRoute component={QuestionAdd} route={'/admin/question-settings/questions/add'} />,
      },
      {
        path: "question-settings/questions/:pgno/edit/:id",
        element: <PrivateRoute component={QuestionEdit} route={'/admin/question-settings/questions/*/edit/*'} />,
      },
      {
        path: "question-settings/questions/view/:id",
        element: <PrivateRoute component={QuestionView} route={'/admin/question-settings/questions/view/*'} />,
      },
      {
        /** Question route end */
      },

      {
        /** Activities Route start */
      },
      {
        path: "activity-forum/activities/list/:pgno",
        element: <PrivateRoute component={ActivitySetTable} route={'/admin/activity-forum/activities/list/*'} />,
      },
      {
        path: "activity-forum/activities/add",
        element: <PrivateRoute component={ActivitySetAdd} route={'/admin/activity-forum/activities/add'} />,
      },
      {
        path: "activity-forum/activities/:pgno/edit/:id",
        element: <PrivateRoute component={ActivitySetEdit} route={'/admin/activity-forum/activities/*/edit/*'} />,
      },
      {
        path: "activity-forum/activities/view/:id",
        element: <PrivateRoute component={ActivitySetView} route={'/admin/activity-forum/activities/view/*'} />,
      },
      {
        path: "activity-forum/topics/add",
        element: <PrivateRoute component={TopicAdd} route={'/admin/activity-forum/topics/add'} />,
      },
      {
        path: "activity-forum/topics/:pgno/edit/:id",
        element: <PrivateRoute component={TopicEdit} route={'/admin/activity-forum/topics/*/edit/*'} />,
      },
      {
        path: "activity-forum/topics/view/:id",
        element: <PrivateRoute component={TopicView} route={'/admin/activity-forum/topics/view/*'} />,
      },
      {
        /**Activities Route End */
      },
      {
        /* News-letter Route Start */
      },
      {
        path: "news-letter/list/:pgno",
        element: <PrivateRoute component={NewsLettersTable} route={'/admin/news-letter/list/*'} />,
      },
      {
        path: "news-letter/add",
        element: <PrivateRoute component={NewsLettersAdd} route={'/admin/news-letter/add'} />,
      },
      {
        path: "news-letter/:pgno/edit/:id",
        element: <PrivateRoute component={NewsLettersEdit} route={'/admin/news-letter/*/edit/*'} />,
      },
      {
        path: "news-letter/view/:id/:pgno",
        element: <PrivateRoute component={NewsLettersView} route={'/admin/news-letter/view/*/*'} />,
      },

      {
        path: "news-letter/promotions/:pgno",
        element: <PrivateRoute component={PromotionsList} />,
      },
      {
        path: "news-letter/promotions/view/:id",
        element: <PrivateRoute component={PromotionsView} />,
      },
      /* Distribution List Route Start */
      {
        path: "distribution-list/list/:pgno",
        element: <PrivateRoute component={DistributionListTable} route={'/admin/distribution-list/list/*'} />,
      },
      {
        path: "distribution-list/add",
        element: <PrivateRoute component={DistributionAdd} route={'/admin/distribution-list/add'} />,
      },
      {
        path: "distribution-list/:pgno/edit/:id",
        element: <PrivateRoute component={DistributionEdit} route={'/admin/distribution-list/*/edit/*'} />,
      },
      {
        path: "distribution-list/view/:id",
        element: <PrivateRoute component={DistributionListView} route={'/admin/distribution-list/view/*'} />,
      },
      {
        path: "distribution-list/users/list/:id/:pgno",
        element: <PrivateRoute component={DistributionUsersList} route={'/admin/distribution-list/view/*/*'} />,
      },
      /* Distribution List Route End */
      {
        /* News-letter Route End */
      },

      /** Room Management Routing */
      {
        path: "room-management/list/:pgno",
        element: <PrivateRoute component={RoomManagementTable} route={'/admin/announcements/list/*'} />,
      },
      {
        path: "room-management/add",
        element: <PrivateRoute component={RoomManagementAdd} route={'/admin/announcements/add'} />,
      },
      {
        path: "room-management/:pgno/edit/:id",
        element: <PrivateRoute component={RoomManagementEdit} route={'/admin/announcements/*/edit/*'} />,
      },
      {
        path: "room-management/view",
        element: <PrivateRoute component={RoomView} route={'/admin/announcements/view/*'} />,
        children: [
          {
            path: "rooms/:id",
            element: <PrivateRoute component={RoomManagementView} route={'/admin/announcements/view/*'} />,
          },
          {
            path: "bookings/list/:id/:pgno",
            element: <PrivateRoute component={BookingTable} route={'/admin/announcements/view/*'} />,
          },
        ],
      },
      {
        path: "room-management/view/booking/:bookingId",
        element: <PrivateRoute component={ViewBookingDetails} route={'/admin/event-management/events/view/*'} />,
      },
      {
        path: "room-management/view/booking/edit/:bookingId",
        element: <PrivateRoute component={EditBooking} route={'/admin/event-management/events/view/*'} />,
      },
      /** Room Management Routing End*/

      /** Announcements Routing */
      {
        path: "announcements/list/:pgno",
        element: <PrivateRoute component={AnnouncementsTable} route={'/admin/announcements/list/*'} />,
      },
      {
        path: "announcements/add",
        element: <PrivateRoute component={AnnouncementsAdd} route={'/admin/announcements/add'} />,
      },
      {
        path: "announcements/:pgno/edit/:id",
        element: <PrivateRoute component={AnnouncementsEdit} route={'/admin/announcements/*/edit/*'} />,
      },
      {
        path: "announcements/view/:id",
        element: <PrivateRoute component={AnnouncementsView} route={'/admin/announcements/view/*'} />,
      },
      /** Announcements Routing End*/

      /* Transactions Routing Start */

      {
        path: "transactions/list/:pgno",
        element: <PrivateRoute component={TransactionsTable} route={'/admin/transactions/list/*'} />,
      },

      { path: "transactions/view/:id", element: <PrivateRoute component={TransactionsView} route={'/admin/transactions/view/*'} /> },

      /* Transactions Routing End */

      /** Media Routing Start*/
      { path: "media", element: <PrivateRoute component={Galary} route={'/admin/media'} /> },

      { path: "media/add", element: <PrivateRoute component={GalleryAdd} route={'/admin/media/add'} /> },

      {
        path: "media/details",
        element: <PrivateRoute component={GalaryDetails} route={'/admin/media/details'} />,
      },

      /** Media Routing End*/

      /** Email Logs Routing Start*/
      { path: "email-logs/list/:pgno", element: <PrivateRoute component={EmailLogs} route={'/admin/email-logs/list/*'} /> },

      { path: "email-logs/view/:id", element: <PrivateRoute component={EmailLogsView} route={'/admin/email-logs/view/*'} /> },

      /** Email Logs Routing End*/
    ],


  },
  { path: "admin/login", element: <PublicRoute component={Login} /> },
  { path: "admin/complete-profile/:validate_string", element: <PublicRoute component={CompleteAdminProfile} /> },
  {
    path: "admin/forget-password",
    element: <PublicRoute component={ForgotPwdPage} />,
  },
  {
    path: "admin/otp-verification/:email",
    element: <PublicRoute component={OtpVerificationPage} />,
  },
  {
    path: "admin/reset-password",
    element: <PublicRoute component={ResetPwdPage} />,
  },
  { path: "admin/*", element: <Error404 /> },


  /**ADMIN ROUTE END */



]);

export default router;
